import authService from '../../services/authService';

// RC19.x Role-Aware UI — the single reusable primitive every other role-aware helper in this
// file/its siblings builds on. One application, one component per page (Dashboard, Candidates,
// Candidate Workspace, Jobs, ...) — behavior differences between roles come from what each
// caller is permitted to see, evaluated here, never from a role-name string comparison or a
// role-specific duplicate page/component. The backend is still the actual security authority
// (every endpoint re-checks); this only decides what to render — never a substitute for a
// server-side check on the same permission code.
//
// `permission` — a single code ("Candidate.Screen") or an array (OR semantics, matching
// AdminRoute's existing array convention in App.js and authService.hasAnyPermission). Omit it
// (undefined/null) for content that's always visible — that's a valid, common case, not an error.
// `minScope` — optional, requires the grant to be at least this wide (OWN < TEAM < DEPARTMENT <
// LOCATION < ORGANIZATION), via authService.hasPermissionAtScope (mirrors the backend's
// canAtScope). Only meaningful with a single-code `permission`.
export function checkPermission(permission, minScope) {
  if (!permission) return true;
  const codes = Array.isArray(permission) ? permission : [permission];
  if (minScope) {
    return codes.some((code) => authService.hasPermissionAtScope(code, minScope));
  }
  return codes.some((code) => authService.hasPermission(code));
}

/**
 * The array-filtering counterpart to the component above — for anything whose *membership*
 * varies by permission rather than its visibility as a single subtree: Tabs (can't have a
 * conditionally-null child, see RoleAwareTab.js), DataTable columns (RoleAwareColumn.js), nav
 * items (RoleAwareMenu.js), dashboard widgets (RoleAwareWidget.js). Each of those re-exports this
 * one implementation under a name that reads naturally at its own call site — not four copies of
 * the same three-line filter.
 */
export function filterByPermission(items) {
  return items.filter((item) => checkPermission(item.permission, item.minScope));
}

/**
 * `<PermissionGate permission="Candidate.Screen">...</PermissionGate>` — renders children only
 * if the check passes, otherwise `fallback` (default: nothing). This is the one primitive behind
 * every "RoleAware" button, quick action, menu item, and dashboard widget in this codebase —
 * those are all the same shape (conditionally render a JSX subtree), so they share this
 * implementation rather than each being a near-identical copy.
 */
const PermissionGate = ({ permission, minScope, fallback = null, children }) => (
  checkPermission(permission, minScope) ? children : fallback
);

export default PermissionGate;
